import {NavigationCancelledChunkError} from '@aboutyou/lazy-imports';
import {Log} from '@aboutyou/mlm';
import React, {ErrorInfo, PropsWithChildren, type JSX} from 'react';
import {ErrorPageInline} from '../../atoms/ErrorPageInline/ErrorPageInline';

interface ErrorDetails {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retried: boolean;
}

export class ErrorBoundary extends React.Component<
  {
    ErrorComponent?: React.ComponentType<{children?: React.ReactNode}> | null;
    renderContext: string;
    children?: React.ReactNode;
  },
  ErrorDetails
> {
  state: ErrorDetails = {
    error: null,
    errorInfo: null,
    retried: false,
  };

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({error, errorInfo});

    // The user navigated away (or backgrounded the tab) while a lazy chunk was
    // still in flight; the browser cancelled the request. There is no real
    // failure to report — keep this off telemetry to avoid drowning real bugs
    // in navigation noise.
    if (error instanceof NavigationCancelledChunkError) return;

    try {
      Log.error(
        '@aboutyou/ui-components',
        `Error Boundary rendered Oops component in context ${this.props.renderContext}`,
        {
          error,
          stackTrace: errorInfo?.componentStack?.substring(0, 150),
        },
      );
    } catch (loggingError) {
      // A throw while React is committing error handling would escalate to the
      // parent boundary instead of being reported here, so swallow it.
    }
  }

  resetError({isRetrying}: {isRetrying: boolean}) {
    this.setState({error: null, errorInfo: null, retried: isRetrying});
  }

  render() {
    return (
      <ErrorContainer
        {...this.props}
        {...this.state}
        resetError={this.resetError.bind(this)}
      />
    );
  }
}

interface ErrorContainerProps extends ErrorDetails {
  ErrorComponent?: React.ComponentType<React.PropsWithChildren> | null;
  renderContext: string;
  resetError: (options: {isRetrying: boolean}) => void;
}

function ErrorContainer({
  error,
  children,
  ErrorComponent,
  resetError,
  retried,
}: PropsWithChildren<ErrorContainerProps>) {
  if (!error) {
    return children as JSX.Element;
  }

  if (ErrorComponent) {
    return <ErrorComponent />;
  }

  if (ErrorComponent === null) {
    return null;
  }

  return (
    <DefaultErrorComponent
      error={error}
      resetError={resetError}
      retried={retried}
    />
  );
}

const DefaultErrorComponent: React.FC<{
  error: Error;
  resetError: (options: {isRetrying: boolean}) => void;
  retried?: boolean;
}> = ({error, resetError, retried}) => {
  return (
    <ErrorPageInline
      subline={process.env.NODE_ENV !== 'production' && error.message}
      resetError={resetError}
      retried={retried}
    />
  );
};
